import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Cookie } from "lucide-react";

const STORAGE_KEY = "penow_cookie_consent_v1";

/**
 * CookieBanner — CNIL/RGPD compliant consent banner.
 *
 * • Shows on first visit until user makes an explicit choice.
 * • Both "Accepter" and "Refuser" are on the same visual level (CNIL requirement).
 * • Refuses = opt-out of PostHog analytics via posthog.opt_out_capturing().
 * • Accepts = opt-in to PostHog analytics.
 * • Choice is stored in localStorage and can be changed later from the
 *   privacy policy page (link included).
 */
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const choice = localStorage.getItem(STORAGE_KEY);
      if (!choice) {
        setVisible(true);
      } else if (choice === "refused" && window.posthog?.opt_out_capturing) {
        window.posthog.opt_out_capturing();
      } else if (choice === "accepted" && window.posthog?.opt_in_capturing) {
        window.posthog.opt_in_capturing();
      }
    } catch {
      setVisible(true);
    }
  }, []);

  const persist = (value) => {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  const accept = () => {
    if (window.posthog?.opt_in_capturing) window.posthog.opt_in_capturing();
    persist("accepted");
  };

  const refuse = () => {
    if (window.posthog?.opt_out_capturing) window.posthog.opt_out_capturing();
    persist("refused");
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:bottom-6 md:left-6 md:right-auto md:max-w-md z-[80] bg-white border border-black/15 shadow-xl"
      role="dialog"
      aria-live="polite"
      aria-label="Consentement aux cookies"
      data-testid="cookie-banner"
    >
      <button
        onClick={() => setVisible(false)}
        className="absolute top-2 right-2 p-1.5 text-anthracite/40 hover:text-anthracite hover:bg-black/5 transition-colors"
        aria-label="Fermer le bandeau (choix reporté)"
        data-testid="cookie-close"
      >
        <X className="w-4 h-4" strokeWidth={1.5} />
      </button>

      <div className="p-5 md:p-6">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 flex items-center justify-center bg-terracotta/10 text-terracotta shrink-0">
            <Cookie className="w-4 h-4" strokeWidth={1.5} />
          </div>
          <div>
            <div className="font-display text-lg text-anthracite leading-tight">
              Cookies & confidentialité
            </div>
            <p className="mt-2 text-sm text-anthracite/70 leading-relaxed">
              Nous utilisons des cookies pour mesurer l'audience du site de
              manière anonyme. Vous pouvez accepter ou refuser à tout moment,
              cela n'a aucune incidence sur votre navigation.
            </p>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          <button
            onClick={accept}
            data-testid="cookie-accept"
            className="flex-1 min-w-[130px] inline-flex justify-center items-center bg-terracotta hover:bg-copper text-white px-4 py-2.5 text-sm tracking-wide transition-colors"
          >
            Accepter
          </button>
          <button
            onClick={refuse}
            data-testid="cookie-refuse"
            className="flex-1 min-w-[130px] inline-flex justify-center items-center border border-anthracite/70 hover:border-terracotta hover:text-terracotta text-anthracite px-4 py-2.5 text-sm tracking-wide transition-colors"
          >
            Refuser
          </button>
        </div>

        <div className="mt-3 text-[11px] text-anthracite/50">
          <Link to="/confidentialite" className="underline hover:text-terracotta">
            En savoir plus sur les cookies et vos droits →
          </Link>
        </div>
      </div>
    </div>
  );
}
